/**
 * PayPal QR Code Payment Component
 * Displays QR code for customer to scan and pay via PayPal
 * Listens to real-time payment status updates
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Portal,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Error as ErrorIcon,
  QrCode2,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import PayPalService from '../services/paypalService';
import { useLanguage } from '../contexts/LanguageContext';

interface PayPalQRPaymentProps {
  open: boolean;
  onClose: () => void;
  approvalUrl: string;
  orderId: string;
  amount: number;
  onPaymentComplete: () => void;
  onPaymentFailed: (error: string) => void;
  onPaymentCancelled?: () => void; // CRITICAL: Callback when payment is cancelled
}

const PayPalQRPayment: React.FC<PayPalQRPaymentProps> = ({
  open,
  onClose,
  approvalUrl,
  orderId,
  amount,
  onPaymentComplete,
  onPaymentFailed,
  onPaymentCancelled,
}) => {
  // Log immediately when component function is called - THIS SHOULD ALWAYS APPEAR
  console.log('🎬 [PAYPAL QR] ========== COMPONENT FUNCTION CALLED ==========');
  console.log('🎬 [PAYPAL QR] Component rendered with props:', {
    open,
    orderId,
    amount,
    hasApprovalUrl: !!approvalUrl,
    approvalUrl: approvalUrl?.substring(0, 50) + '...',
    timestamp: new Date().toISOString(),
  });
  
  // CRITICAL: Log if component is being called but open is false
  if (!open) {
    console.log('⏸️ [PAYPAL QR] Component called but open=false, returning null');
  } else {
    console.log('✅ [PAYPAL QR] Component called with open=true, will render Dialog');
  }
  
  // Force re-render if open changes
  const [forceRender, setForceRender] = useState(0);
  useEffect(() => {
    if (open) {
      console.log('🔄 [PAYPAL QR] Dialog should be open, forcing render...');
      setForceRender(prev => prev + 1);
    }
  }, [open]);
  
  const { t, language } = useLanguage();
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'completed' | 'failed' | 'refunded'>('pending');
  const [isPolling, setIsPolling] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(11 * 60); // 11 minutes in seconds
  const paypalService = PayPalService.getInstance();

  // CRITICAL: Handle payment cancellation when dialog closes without payment
  const handleCancelPayment = async () => {
    if (paymentStatus === 'pending') {
      console.log('❌ [PAYPAL QR] Payment cancelled by user');
      try {
        // Update order status to cancelled
        const { doc, updateDoc } = await import('../services/firestoreCompat');
        const { db } = await import('../services/firebaseConfig');
        const orderRef = doc(db, 'orders', orderId);
        await updateDoc(orderRef, {
          payment_status: 'failed',
          status: 'cancelled',
          cancelled_at: new Date(),
          cancellation_reason: 'Payment cancelled by user or timed out',
        });
        console.log('✅ [PAYPAL QR] Order marked as cancelled');
      } catch (error) {
        console.error('❌ [PAYPAL QR] Error cancelling order:', error);
      }
      
      if (onPaymentCancelled) {
        onPaymentCancelled();
      }
    }
    onClose();
  };

  // Log when component mounts/opens
  useEffect(() => {
    console.log('🔍 [PAYPAL QR] Component mounted/updated', {
      open,
      orderId,
      amount,
      hasApprovalUrl: !!approvalUrl,
    });
    
    if (open) {
      console.log('✅ [PAYPAL QR] QR Payment dialog opened', {
        orderId,
        amount,
        hasApprovalUrl: !!approvalUrl,
        approvalUrl: approvalUrl?.substring(0, 50) + '...',
      });
    } else {
      console.log('🔒 [PAYPAL QR] QR Payment dialog closed');
    }
  }, [open, orderId, amount, approvalUrl]);

  // CRITICAL: Timeout countdown timer
  useEffect(() => {
    if (!open || paymentStatus !== 'pending') {
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Timeout reached - cancel payment
          console.log('⏰ [PAYPAL QR] Payment timeout reached (11 minutes)');
          handleCancelPayment();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, paymentStatus, orderId]);

  // Reset timer when dialog opens
  useEffect(() => {
    if (open) {
      setTimeRemaining(11 * 60); // Reset to 11 minutes
    }
  }, [open]);

  // Listen to payment status changes in real-time
  useEffect(() => {
    if (!open || !orderId) {
      console.log('⏸️ [PAYPAL QR] Skipping listener setup:', { open, orderId });
      return;
    }

    console.log('📡 [PAYPAL QR] Starting payment status listener for order:', orderId);
    console.log('📡 [PAYPAL QR] Component mounted, starting polling...');

    // Track initial status to prevent false positives
    let initialStatusChecked = false;
    let initialStatus: string | null = null;
    let hasSeenPendingStatus = false; // Track if we've ever seen 'pending' status (real order)

    // Set up real-time listener
    const unsubscribe = paypalService.listenToPaymentStatus(orderId, (status) => {
      console.log('📡 [PAYPAL QR] Payment status updated:', status);
      
      // Check initial status on first callback
      if (!initialStatusChecked) {
        initialStatusChecked = true;
        console.log('🔍 [PAYPAL QR] Initial payment status:', status);
        
        // CRITICAL: If status is 'completed' on first check, this is a bug!
        // The order should be created with 'pending' status for PayPal/Card payments
        if (status === 'completed') {
          console.error('⚠️⚠️⚠️ [PAYPAL QR] SECURITY WARNING: Order was created with payment_status="completed"!');
          console.error('⚠️⚠️⚠️ [PAYPAL QR] This should be "pending" for PayPal/Card payments!');
          console.error('⚠️⚠️⚠️ [PAYPAL QR] Resetting to "pending" and continuing to monitor for actual payment.');
          // Reset to pending to prevent false positive
          initialStatus = 'pending'; // CRITICAL: Set initialStatus to 'pending' so polling logic works
          hasSeenPendingStatus = false; // Mark that we haven't seen real pending status yet
          setPaymentStatus('pending');
          
          // CRITICAL: Also fix the Firebase order status (async, don't wait)
          (async () => {
            try {
              const { doc, updateDoc } = await import('../services/firestoreCompat');
              const { db } = await import('../services/firebaseConfig');
              const orderRef = doc(db, 'orders', orderId);
              await updateDoc(orderRef, {
                payment_status: 'pending',
              });
              console.log('✅ [PAYPAL QR] Fixed Firebase order status from "completed" to "pending"');
            } catch (error) {
              console.error('❌ [PAYPAL QR] Error fixing Firebase order status:', error);
            }
          })();
          
          return; // Don't trigger payment complete callback, but continue polling
        } else if (status === 'failed') {
          // CRITICAL: If status is 'failed' on first check, this is also a bug!
          // The order should be created with 'pending' status, not 'failed'
          console.error('⚠️⚠️⚠️ [PAYPAL QR] ERROR: Order was created with payment_status="failed"!');
          console.error('⚠️⚠️⚠️ [PAYPAL QR] This should be "pending" for PayPal/Card payments!');
          console.error('⚠️⚠️⚠️ [PAYPAL QR] Resetting to "pending" and continuing to monitor for actual payment.');
          // Reset to pending - this is likely a bug in order creation
          initialStatus = 'pending'; // CRITICAL: Set initialStatus to 'pending' so polling logic works
          hasSeenPendingStatus = false; // Mark that we haven't seen real pending status yet
          setPaymentStatus('pending');
          
          // CRITICAL: Also fix the Firebase order status (async, don't wait)
          (async () => {
            try {
              const { doc, updateDoc } = await import('../services/firestoreCompat');
              const { db } = await import('../services/firebaseConfig');
              const orderRef = doc(db, 'orders', orderId);
              await updateDoc(orderRef, {
                payment_status: 'pending',
              });
              console.log('✅ [PAYPAL QR] Fixed Firebase order status from "failed" to "pending"');
            } catch (error) {
              console.error('❌ [PAYPAL QR] Error fixing Firebase order status:', error);
            }
          })();
          
          return; // Don't trigger payment failed callback, but continue polling
        } else {
          // Normal case: status is 'pending'
          initialStatus = status;
          hasSeenPendingStatus = (status === 'pending'); // Track if we've seen real pending status
        }
      }
      
      // CRITICAL: Don't update UI status if this is a buggy initial 'completed' status
      // Only update if we've properly initialized (initialStatus is set)
      if (initialStatus !== null) {
        setPaymentStatus(status as 'pending' | 'completed' | 'failed' | 'refunded');
      }
      
      // Track if we've seen pending status (real order, not bug)
      if (status === 'pending') {
        hasSeenPendingStatus = true;
      }

      // CRITICAL: Only trigger callbacks if:
      // 1. Status is 'completed'
      // 2. Initial status was 'pending' (not buggy 'completed')
      // 3. We've seen the pending status at least once (to avoid false positives from bug)
      // 4. Initial status check has been completed (initialStatus is not null)
      if (status === 'completed' && initialStatus === 'pending' && hasSeenPendingStatus && initialStatus !== null) {
        console.log('✅ [PAYPAL QR] Payment completed (status changed from pending to completed)');
        setIsPolling(false);
        // Wait 3 seconds to show success message, then call callback
        setTimeout(() => {
          console.log('✅ [PAYPAL QR] Calling onPaymentComplete callback...');
          onPaymentComplete();
        }, 3000); // Show success message for 3 seconds
      } else if (status === 'failed' && initialStatus === 'pending' && hasSeenPendingStatus) {
        // CRITICAL: Only show failed if status actually changed from pending to failed
        // This prevents false positives from orders created with 'failed' status
        console.log('❌ [PAYPAL QR] Payment failed (status changed from pending to failed)');
        setIsPolling(false);
        onPaymentFailed(t('paypal.payment_failed'));
      }
      // If status is 'failed' but initialStatus was also 'failed', ignore it (bug case)
    });

    // Also poll PayPal order status directly and auto-capture when approved
    setIsPolling(true);
    console.log('🔄 [PAYPAL QR] Starting polling mechanism...');
    let paypalOrderId: string | null = null;
    let pollAttempts = 0;
    let isPaymentCompleted = false; // CRITICAL: Flag to prevent multiple completions
    
    // First, get PayPal order ID from Firestore order
    const getPayPalOrderId = async () => {
      try {
        const { doc, getDoc } = await import('../services/firestoreCompat');
        const { db } = await import('../services/firebaseConfig');
        const orderDoc = await getDoc(doc(db, 'orders', orderId));
        if (orderDoc.exists()) {
          const orderData = orderDoc.data();
          const paypalId = orderData.paypal_order_id || null;
          console.log('🔍 [PAYPAL QR] Retrieved PayPal Order ID from Firestore:', paypalId);
          return paypalId;
        } else {
          console.warn('⚠️ [PAYPAL QR] Firebase order not found:', orderId);
        }
      } catch (error) {
        console.error('❌ [PAYPAL QR] Error getting PayPal order ID:', error);
      }
      return null;
    };
    
    // Initialize PayPal order ID immediately
    getPayPalOrderId().then(id => {
      paypalOrderId = id;
      console.log('📋 [PAYPAL QR] Initial PayPal Order ID:', paypalOrderId);
    });
    
    // CRITICAL FIX: Store interval reference in a way that's accessible everywhere
    let pollIntervalRef: NodeJS.Timeout | null = null;
    
    const pollInterval = setInterval(async () => {
      // CRITICAL: Stop polling if payment is already completed
      if (isPaymentCompleted) {
        console.log('⏸️ [PAYPAL QR] Payment already completed, stopping poll...');
        if (pollIntervalRef) {
          clearInterval(pollIntervalRef);
          pollIntervalRef = null;
        }
        return;
      }
      pollAttempts++;
      console.log(`🔄 [PAYPAL QR] Poll attempt #${pollAttempts}...`);
      
      try {
        // Get PayPal order ID if not already retrieved
        if (!paypalOrderId) {
          console.log('🔍 [PAYPAL QR] PayPal Order ID not found, fetching...');
          paypalOrderId = await getPayPalOrderId();
          if (!paypalOrderId) {
            console.log('⏳ [PAYPAL QR] Waiting for PayPal order ID... (attempt', pollAttempts, ')');
            return;
          }
          console.log('✅ [PAYPAL QR] PayPal Order ID retrieved:', paypalOrderId);
        }
        
        // Check PayPal order status directly
        const { HAS_VALID_FIREBASE_CONFIG } = await import('../services/firebaseConfig');
        if (!HAS_VALID_FIREBASE_CONFIG) {
          console.warn('[PAYPAL QR] paypalGetOrder skipped — Firebase Functions not configured');
          return;
        }
        console.log('📞 [PAYPAL QR] Calling paypalGetOrder function with ID:', paypalOrderId);
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const getOrder = httpsCallable(functions, 'paypalGetOrder');
        
        const result = await getOrder({ paypalOrderId });
        const orderData = result.data as any;
        
        console.log('📊 [PAYPAL QR] PayPal order status response:', {
          paypalOrderId,
          status: orderData.status,
          firebaseStatus: orderData.firebase_status,
          fullResponse: orderData,
        });
        
        // If order is APPROVED, automatically capture it
        if (orderData.status === 'APPROVED') {
          console.log('✅ [PAYPAL QR] Order approved! Auto-capturing...');
          try {
            const captureResult = await paypalService.captureOrder(paypalOrderId);
            if (captureResult.success) {
              console.log('✅ [PAYPAL QR] Payment captured successfully!', {
                orderId: captureResult.orderId,
                status: captureResult.status,
                transactionId: captureResult.transactionId,
                amount: captureResult.amount,
              });
              
              // CRITICAL: Check capture result status immediately
              if (captureResult.status === 'COMPLETED') {
                console.log('✅ [PAYPAL QR] Capture result shows COMPLETED - payment is done!');
                
                // Mark that we've seen real payment
                hasSeenPendingStatus = true;
                
                // Wait a moment for Firebase to update, then verify and complete
                setTimeout(async () => {
                  // Verify PayPal order status one more time
                  try {
                    const { HAS_VALID_FIREBASE_CONFIG } = await import('../services/firebaseConfig');
                    if (!HAS_VALID_FIREBASE_CONFIG) throw new Error('Firebase Functions not configured');
                    const { getFunctions, httpsCallable } = await import('firebase/functions');
                    const functions = getFunctions();
                    const verifyGetOrder = httpsCallable(functions, 'paypalGetOrder');
                    const verifyResult = await verifyGetOrder({ paypalOrderId });
                    const verifyData = verifyResult.data as any;
                    console.log('📊 [PAYPAL QR] Verification after capture:', {
                      status: verifyData.status,
                      firebaseStatus: verifyData.firebase_status,
                    });
                    
                    if (verifyData.status === 'COMPLETED') {
                      console.log('✅ [PAYPAL QR] PayPal order confirmed COMPLETED!');
                      isPaymentCompleted = true; // CRITICAL: Mark as completed
                      setPaymentStatus('completed');
                      setIsPolling(false);
                      if (pollIntervalRef) {
                        clearInterval(pollIntervalRef);
                        pollIntervalRef = null;
                      }
                      
                      // Show success message for 3 seconds, then complete
                      setTimeout(() => {
                        console.log('✅ [PAYPAL QR] Calling onPaymentComplete callback...');
                        onPaymentComplete();
                      }, 3000);
                    }
                  } catch (verifyError) {
                    console.error('❌ [PAYPAL QR] Error verifying after capture:', verifyError);
                    // Still trigger completion since capture was successful
                    isPaymentCompleted = true; // CRITICAL: Mark as completed
                    setPaymentStatus('completed');
                    setIsPolling(false);
                    if (pollIntervalRef) {
                      clearInterval(pollIntervalRef);
                      pollIntervalRef = null;
                    }
                    setTimeout(() => {
                      console.log('✅ [PAYPAL QR] Calling onPaymentComplete callback (after capture)...');
                      onPaymentComplete();
                    }, 3000);
                  }
                }, 1000); // Wait 1 second for Firebase to update
              } else {
                console.warn('⚠️ [PAYPAL QR] Capture successful but status is not COMPLETED:', captureResult.status);
              }
            } else {
              console.error('❌ [PAYPAL QR] Capture failed:', captureResult.error);
            }
          } catch (captureError) {
            console.error('❌ [PAYPAL QR] Error capturing order:', captureError);
          }
        }
        
        // CRITICAL: If PayPal order status is COMPLETED, this is real payment
        // BUT: Only trigger if we've properly initialized (initialStatus is not null)
        // AND: Only if initial status was 'pending' (not buggy 'completed')
        if (orderData.status === 'COMPLETED' && !isPaymentCompleted && initialStatus !== null && initialStatus === 'pending') {
          console.log('✅ [PAYPAL QR] PayPal order is COMPLETED - this is real payment!');
          isPaymentCompleted = true; // CRITICAL: Mark as completed to prevent multiple triggers
          
          if (!hasSeenPendingStatus) {
            // This is real payment detected via PayPal API, even if Firebase had buggy status
            // But we still require initialStatus === 'pending' to ensure it wasn't a buggy order
            console.log('✅ [PAYPAL QR] Real payment detected via PayPal API, triggering completion...');
            hasSeenPendingStatus = true;
            setPaymentStatus('completed');
            setIsPolling(false);
            if (pollIntervalRef) {
              clearInterval(pollIntervalRef);
              pollIntervalRef = null;
            }
            setTimeout(() => {
              console.log('✅ [PAYPAL QR] Calling onPaymentComplete callback...');
              onPaymentComplete();
            }, 3000);
            return; // Exit early, payment is complete
          } else if (hasSeenPendingStatus && initialStatus === 'pending') {
            // Normal flow: status changed from pending to completed
            console.log('✅ [PAYPAL QR] Payment completed (status changed from pending to completed)');
            setPaymentStatus('completed');
            setIsPolling(false);
            if (pollIntervalRef) {
              clearInterval(pollIntervalRef);
              pollIntervalRef = null;
            }
            setTimeout(() => {
              console.log('✅ [PAYPAL QR] Calling onPaymentComplete callback...');
              onPaymentComplete();
            }, 3000);
            return; // Exit early, payment is complete
          }
        }
        
        // Check Firestore order status
        const { doc, getDoc } = await import('../services/firestoreCompat');
        const { db } = await import('../services/firebaseConfig');
        const orderDoc = await getDoc(doc(db, 'orders', orderId));
        if (orderDoc.exists()) {
          const orderData = orderDoc.data();
          const status = orderData.payment_status;
          
          console.log('🔍 [PAYPAL QR] Firestore order status check:', {
            status,
            initialStatus,
            isInitialCompleted: initialStatus === 'completed',
            shouldStop: status === 'completed' && initialStatus === 'pending', // Only stop if it changed from pending to completed
          });
          
                  // Only stop polling if status changed from pending to completed (actual payment)
                  // AND we've seen the pending status at least once (to avoid false positives from bug)
                  if (status === 'completed' && initialStatus === 'pending' && hasSeenPendingStatus && !isPaymentCompleted) {
                    console.log('✅ [PAYPAL QR] Payment detected! Stopping polling.');
                    isPaymentCompleted = true; // CRITICAL: Mark as completed
                    if (pollIntervalRef) {
                      clearInterval(pollIntervalRef);
                      pollIntervalRef = null;
                    }
                    setIsPolling(false);
                  } else if (status === 'failed' && !isPaymentCompleted) {
                    console.log('❌ [PAYPAL QR] Payment failed. Stopping polling.');
                    isPaymentCompleted = true; // CRITICAL: Mark as completed (failed)
                    if (pollIntervalRef) {
                      clearInterval(pollIntervalRef);
                      pollIntervalRef = null;
                    }
                    setIsPolling(false);
                  }
                  // Continue polling if status is still 'pending' or if initial status was 'completed' (bug case)
                }
              } catch (error) {
                console.error('❌ [PAYPAL QR] Error polling status:', error);
              }
            }, 3000); // Poll every 3 seconds
            
            // CRITICAL: Store interval reference
            pollIntervalRef = pollInterval;

            // Cleanup
            return () => {
              console.log('🧹 [PAYPAL QR] Cleaning up polling and listeners...');
              unsubscribe();
              if (pollIntervalRef) {
                clearInterval(pollIntervalRef);
                pollIntervalRef = null;
              }
              setIsPolling(false);
              isPaymentCompleted = false; // Reset for next time
            };
  }, [open, orderId, onPaymentComplete, onPaymentFailed, language]);

  const handleOpenInBrowser = () => {
    window.open(approvalUrl, '_blank');
  };

  // Don't render if not open
  if (!open) {
    return null;
  }

  console.log('🎨 [PAYPAL QR] About to render Dialog, open:', open, 'forceRender:', forceRender);
  
  return (
    <Portal>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 9999, // Very high z-index to ensure it's above everything
          '& .MuiDialog-container': {
            zIndex: 9999,
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            zIndex: 9999,
            position: 'relative',
          },
        }}
        BackdropProps={{
          sx: {
            zIndex: 9998,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCode2 sx={{ color: '#FF9800' }} />
          {t('paypal.qr_payment')}
        </Typography>
        <IconButton onClick={handleCancelPayment} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Inject global CSS for logo animation */}
        <style>{`
          @keyframes paypal-logo-rotate {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
          .paypal-logo-spinner {
            animation: paypal-logo-rotate 2s linear infinite;
            transform-origin: center;
          }
        `}</style>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {/* Payment Status Indicator */}
          {paymentStatus === 'pending' && (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                {/* App Logo with rotation animation */}
                <Box
                  className="paypal-logo-spinner"
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src="/saheeh_logo_w.svg"
                    alt="SAHEEH Logo"
                    style={{
                      width: '80px',
                      height: 'auto',
                      maxWidth: '80px',
                    }}
                  />
                </Box>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    fontSize: '1.1rem',
                    color: '#333',
                  }}
                >
                  {t('paypal.waiting_for_payment')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('paypal.scan_qr_code')}
                </Typography>
              </Box>
            </Box>
          )}

          {paymentStatus === 'completed' && (
            <Box sx={{ mb: 3 }}>
              <CheckCircle sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#4CAF50', mb: 1 }}>
                {t('paypal.payment_successful')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('paypal.payment_completed_message')}
              </Typography>
            </Box>
          )}

          {paymentStatus === 'failed' && (
            <Box sx={{ mb: 3 }}>
              <ErrorIcon sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336', mb: 1 }}>
                {t('paypal.payment_failed')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('paypal.payment_failed_message')}
              </Typography>
            </Box>
          )}

          {/* QR Code */}
          {paymentStatus === 'pending' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                p: 3,
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                mb: 3,
              }}
            >
              <QRCodeSVG
                value={approvalUrl}
                size={256}
                level="H"
                includeMargin={true}
              />
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                {t('paypal.scan_qr_complete')}
              </Typography>
            </Box>
          )}

          {/* Amount Display */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              €{amount.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('paypal.amount_to_pay')}
            </Typography>
          </Box>

          {/* Time Remaining Display */}
          {paymentStatus === 'pending' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {`${t('paypal.time_remaining')} ${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, '0')}`}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          {paymentStatus === 'pending' && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleOpenInBrowser}
                sx={{
                  borderColor: '#FF9800',
                  color: '#FF9800',
                  '&:hover': {
                    borderColor: '#F57C00',
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  },
                }}
              >
                {t('paypal.open_in_browser')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancelPayment}
                sx={{
                  borderColor: '#f44336',
                  color: '#f44336',
                  '&:hover': {
                    borderColor: '#d32f2f',
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  },
                }}
              >
                {t('paypal.cancel_order')}
              </Button>
            </Box>
          )}

          {/* Status Message */}
          {isPolling && paymentStatus === 'pending' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {t('paypal.monitoring_status')}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
    </Portal>
  );
};

export default PayPalQRPayment;

